import { environment } from '../../environments/environment';
import {
  AddIndividual,
  ApiResponse,
  ApplicationVerificationPayload,
  ConsentDispatchResultItem,
  GetApplicationPartiesPayload,
  GetApplicationindividualsPayload,
  KycStatus,
  PrivacyConsentBypassResult,
  SaveApplicationPartiesData,
  SaveApplicationPartiesSummary,
  SyncIndividualKycFromFrankieOnePayload,
  UpdateApplicationIndividualDigitalIdMappingInput,
  UpdateApplicationIndividualDigitalIdMappingPayload,
  UpdateIndividualPayload, VerifyOneApplicationIndividualPayload,
  ApplicationIndividual,
} from '@portal-workspace/grow-shared-library';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { httpOptions } from '@portal-workspace/grow-ui-library';
import { PayloadApiResponse } from '@portal-workspace/grow-shared-library';
import { map } from 'rxjs/operators';

const apiHost = environment.api2Host;
const URL_CHECK_APPLICATION_VERIFICATION = (applicationId: number) => `${apiHost}/api2/digitalId/checkApplicationVerification/${applicationId}`;
const URL_GET_APPLICATION_INDIVIDUALS = (applicationId: number) => `${apiHost}/api2/digitalId/getApplicationIndividuals/${applicationId}`;
const URL_VERIFY_ONE_APPLICATION_INDIVIDUAL = (applicationId: number, individualId: string) => `${apiHost}/api2/digitalId/verifyOneApplicationIndividual/${applicationId}/${individualId}`;
const URL_UPDATE_PRIMARY_CONTACT = (applicationId: number, individualId: string) => `${apiHost}/api2/digitalId/updatePrimaryContact/${applicationId}/${individualId}`;
const URL_ADD_INDIVIDUAL = (applicationId: number) => `${apiHost}/api2/digitalId/addIndividual/${applicationId}`;
const URL_UPDATE_APPLICATION_INDIVIDUAL_DIGITAL_ID_MAPPING = (applicationId: number, individualId: string) => `${apiHost}/api2/digitalId/updateApplicationIndividualDigitalIdMapping/${applicationId}/${individualId}`;
const URL_SEND_VERIFICATION_SMS = (applicationId: number, individualId: string) => `${apiHost}/api2/digitalId/sendSms/${applicationId}/${individualId}`;
const URL_SYNC_INDIVIDUAL_KYC_FROM_FRANKIEONE = () => `${apiHost}/api2/sync-individual-kyc-from-frankieone`;
const URL_REISSUE_CONSENT_FORM = () => `${apiHost}/api2/dispatch-consent-form`;
const URL_BYPASS_PRIVACY_CONSENT = () => `${apiHost}/api2/bypass-privacy-consent`;
const URL_SAVE_APPLICATION_PARTIES = () => `${apiHost}/api2/save-application-parties`;
const URL_GET_APPLICATION_PARTIES = () => `${apiHost}/api2/get-application-parties`;


@Injectable()
export class DigitalIdService {

  constructor(private httpClient: HttpClient) { }

  checkApplicationVerification(applicationId: number): Observable<PayloadApiResponse<ApplicationVerificationPayload>> {
    return this.httpClient.get<PayloadApiResponse<ApplicationVerificationPayload>>(
      URL_CHECK_APPLICATION_VERIFICATION(applicationId), httpOptions());
  }

  updatePrimaryContact(applicationId: number, individualId: string | undefined):
    Observable<PayloadApiResponse<UpdateIndividualPayload>> {
    return this.httpClient.post<PayloadApiResponse<UpdateIndividualPayload>>(
      URL_UPDATE_PRIMARY_CONTACT(applicationId, individualId!), {}, httpOptions());
  }

  updateApplicationIndividualDigitalIdMappingFn = (applicationId: number, individualId: string, input: UpdateApplicationIndividualDigitalIdMappingInput) => {
    return this.updateApplicationIndividualDigitalIdMapping(applicationId, individualId, input).pipe(map(r => r.payload));
  }
  updateApplicationIndividualDigitalIdMapping(applicationId: number, individualId: string,
    input: UpdateApplicationIndividualDigitalIdMappingInput):
    Observable<PayloadApiResponse<UpdateApplicationIndividualDigitalIdMappingPayload>> {
    return this.httpClient.post<PayloadApiResponse<UpdateApplicationIndividualDigitalIdMappingPayload>>(
      URL_UPDATE_APPLICATION_INDIVIDUAL_DIGITAL_ID_MAPPING(applicationId, individualId), input, httpOptions()
    );
  }

  getApplicationIndividualsFn = (applicationId: number) => {
    return this.getApplicationIndividuals(applicationId).pipe(map(r => r.payload));
  }
  getApplicationIndividuals(applicationId: number): Observable<PayloadApiResponse<GetApplicationindividualsPayload>> {
    return this.httpClient.get<PayloadApiResponse<GetApplicationindividualsPayload>>(
      URL_GET_APPLICATION_INDIVIDUALS(applicationId),
      {
        ...httpOptions(),
        params: {
          t: String(Math.random())
        }
      });
  }

  verifyOneApplicationIndividualFn = (applicationId: number, individualId: string) => {
    return this.verifyOneApplicationIndividual(applicationId, individualId).pipe(map(r => r.payload));
  }
  verifyOneApplicationIndividual(applicationId: number, individualId: string):
    Observable<PayloadApiResponse<VerifyOneApplicationIndividualPayload>> {
    return this.httpClient.get<PayloadApiResponse<VerifyOneApplicationIndividualPayload>>(
      URL_VERIFY_ONE_APPLICATION_INDIVIDUAL(applicationId, individualId), httpOptions());
  }

  addIndividualFn = (applicationId: number, data: AddIndividual) => {
    return this.addIndividual(applicationId, data).pipe(
      map(r => r.payload)
    );
  }
  addIndividual(applicationId: number, data: AddIndividual): Observable<PayloadApiResponse<ApplicationIndividual>> {
    return this.httpClient.post<PayloadApiResponse<ApplicationIndividual>>(
      URL_ADD_INDIVIDUAL(applicationId), data, httpOptions());
  }

  sendVerificationSmsFn = (applicationId: number, individualId: string) => {
    return this.sendVerificationSms(applicationId, individualId).pipe(map(r => r.payload));
  }
  sendVerificationSms(applicationId: number, individualId: string):
    Observable<PayloadApiResponse<{ verificationUrl: string; status: string }>> {
    return this.httpClient.post<PayloadApiResponse<{ verificationUrl: string; status: string }>>(
      URL_SEND_VERIFICATION_SMS(applicationId, individualId), {}, httpOptions());
  }

  updateIndividualKycStatus(applicationId: number, individualId: string, kycStatus: KycStatus, note: string): Observable<ApiResponse> {
    return this.httpClient.post<ApiResponse>(`${apiHost}/api2/update-individual-kyc-status`, { applicationId, individualId, kycStatus, note }, httpOptions());
  }

  syncIndividualKycFromFrankieOneFn = (applicationId: number, individualId: string) => {
    return this.syncIndividualKycFromFrankieOne(applicationId, individualId);
  }
  syncIndividualKycFromFrankieOne(applicationId: number, individualId: string):
    Observable<PayloadApiResponse<SyncIndividualKycFromFrankieOnePayload>> {
    return this.httpClient.post<PayloadApiResponse<SyncIndividualKycFromFrankieOnePayload>>(
      URL_SYNC_INDIVIDUAL_KYC_FROM_FRANKIEONE(), { applicationId, individualId }, httpOptions());
  }

  // Re-generate and re-dispatch the privacy consent form for a signatory. Pass reissueSigned when the
  // current form is already accepted: the server supersedes it and requires a reason for the audit log.
  reissueConsentForm(
    applicationId: number,
    individualId: string,
    options?: { reissueSigned: boolean; reason: string },
  ): Observable<PayloadApiResponse<ConsentDispatchResultItem>> {
    return this.httpClient.post<PayloadApiResponse<ConsentDispatchResultItem>>(
      URL_REISSUE_CONSENT_FORM(), { applicationId, individualId, ...options }, httpOptions());
  }

  // Admin-only: record a signatory's privacy consent as complete without a customer signature.
  // The reason is mandatory and is kept on the consent record and the application audit log.
  bypassPrivacyConsent(
    applicationId: number,
    individualId: string,
    reason: string,
  ): Observable<PayloadApiResponse<PrivacyConsentBypassResult>> {
    return this.httpClient.post<PayloadApiResponse<PrivacyConsentBypassResult>>(
      URL_BYPASS_PRIVACY_CONSENT(), { applicationId, individualId, reason }, httpOptions());
  }

  // Consolidated save for the KYC/AML tab: persist all dirty individuals in one request; the server
  // re-verifies digital ID and invalidates/reissues privacy consent per affected individual.
  saveApplicationPartiesFn = (data: SaveApplicationPartiesData) => {
    return this.saveApplicationParties(data).pipe(map(r => r.payload));
  }
  saveApplicationParties(data: SaveApplicationPartiesData): Observable<PayloadApiResponse<SaveApplicationPartiesSummary>> {
    return this.httpClient.post<PayloadApiResponse<SaveApplicationPartiesSummary>>(
      URL_SAVE_APPLICATION_PARTIES(), data, httpOptions());
  }

  // The read counterpart of saveApplicationParties: individuals and entities in the redesigned
  // shape, with every role flag intact. Prefer this over the party arrays hanging off the hydrated
  // application object, which come from the lossy legacy projection.
  getApplicationPartiesFn = (applicationId: number) => {
    return this.getApplicationParties(applicationId).pipe(map(r => r.payload));
  }
  getApplicationParties(applicationId: number): Observable<PayloadApiResponse<GetApplicationPartiesPayload>> {
    return this.httpClient.post<PayloadApiResponse<GetApplicationPartiesPayload>>(
      URL_GET_APPLICATION_PARTIES(), { applicationId }, httpOptions());
  }

}

